import React, { useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import "../../styles/ServiceModal.css";
import { useSelector } from "react-redux";
import translations from "../../assets/locals/translations";
import {
  FaCalendarCheck,
  FaClock,
  FaRegClock,
  FaSave,
  FaTimes,
  FaTractor,
} from "react-icons/fa";

const ServiceModal = ({ show, onHide, initialData, onSave }) => {
  const currentLanguage = useSelector((state) => state.language.language);
  const t = translations[currentLanguage] || translations["en"];

  const [formData, setFormData] = useState({});

  useEffect(() => {
    if (show) {
      setFormData(initialData || {});
    }
  }, [show, initialData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  // build settings config with translated titles/labels
  const settingsConfig = useMemo(
    () => ({
      schedule_followup: {
        id: 1,
        title: t.nextFollowUp || "Schedule Next",
        icon: FaCalendarCheck,
        fields: [
          {
            id: "next_followup_date",
            label: t.nextFollowUpDate || "Next Follow Up Date",
            type: "date",
            required: true,
          },
          {
            id: "schedule_notes",
            label: t.notes || "Notes / Remark",
            type: "textarea",
          },
        ],
      },
      service_hours: {
        id: 2,
        title: t.serviceHours || "Service Hours",
        icon: FaClock,
        fields: [
          {
            id: "last_service_hour",
            label: t.lastServiceHours || "Last Service Hours",
            type: "text",
            required: true,
          },
          {
            id: "last_service_notes",
            label: t.notes || "Notes / Remark",
            type: "textarea",
          },
        ],
      },
      tractor_status: {
        id: 4,
        title: t.tractorSold || "Tractor Sold",
        icon: FaTractor,
        fields: [
          {
            id: "has_tractor",
            label: t.tractorSoldStatus || "Tractor Sold Status",
            type: "select",
            options: [
              { label: t.selectOption || "Select an option...", value: "" },
              { label: t.yes || "Yes", value: "yes" },
              { label: t.no || "No", value: "no" },
            ],
            required: true,
          },
          {
            id: "tractor_notes",
            label: t.additionalInfo || "Additional Info / Remarks",
            type: "textarea",
          },
        ],
      },
    }),
    [t]
  );

  const sectionIDs = useMemo(
    () => Object.keys(settingsConfig),
    [settingsConfig]
  );
  const [selectedSectionId, setSelectedSectionId] = useState(
    sectionIDs[0] || null
  );

  // Update selectedSection when selectedSectionId or config changes
  const selectedSection = useMemo(
    () =>
      settingsConfig[selectedSectionId] || { title: "Loading...", fields: [] },
    [selectedSectionId, settingsConfig]
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    // Construct data specific to the current tab, plus any global data
    const dataToSave = { ...formData, tab: selectedSectionId };

    // Optional: Clear data from other tabs before saving, or handle this on the backend
    // Example: if you want to ensure only relevant 'notes' are saved:
    // const currentSectionFields = settingsConfig[selectedSectionId].fields.map(f => f.id);
    // const relevantFormData = {};
    // Object.keys(formData).forEach(key => {
    //     if (currentSectionFields.includes(key) || !['notes', 'tractor_notes'].includes(key) /* add other common fields */ ) {
    //         relevantFormData[key] = formData[key];
    //     }
    // });
    // if (selectedSectionId === 'schedule_followup' && formData.notes) relevantFormData.schedule_notes = formData.notes;
    // etc. This can get complex. Simpler to send all and let backend pick.

    console.log("Submitting Data:", dataToSave);
    onSave(dataToSave);
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      dialogClassName="sm-dialog"
      contentClassName="sm-scoped"
      backdrop="static"
      keyboard={false}
      centered
      scrollable
      size="xl"
    >
      <Modal.Body className="sm-modal-body">
        <div className="sm-layout">
          {/* --- Sidebar --- */}
          <aside className="sm-sidebar">
            <h5 className="sm-sidebar__title">{t.serviceFollowUp || "Service Follow Up"}</h5>
            <nav>
              <ul className="sm-sidebar__nav">
                {sectionIDs.map((id) => {
                  const section = settingsConfig[id];
                  const Icon = section.icon;
                  const isActive = selectedSectionId === id;
                  return (
                    <li className="sm-sidebar__item" key={id}>
                      <button
                        type="button"
                        onClick={() => setSelectedSectionId(id)}
                        className={`sm-sidebar__button ${isActive ? "sm-sidebar__button--active" : ""
                          }`}
                      >
                        {Icon && <Icon className="sm-sidebar__button-icon" />}
                        <span>{section.title}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </nav>
            <hr className="sm-sidebar__hr" />
            <div className="sm-sidebar__customer-info">
              <h6 className="sm-customer-info__title">{t.customerDetails || "Customer Details"}:</h6>
              <div className="sm-customer-info__item">
                <strong>{t.name || "Name"}:</strong>{" "}
                <span>{initialData?.customer_name || "—"}</span>
              </div>
              <div className="sm-customer-info__item">
                <strong>{t.village || "Village"}:</strong>{" "}
                <span>{initialData?.village || "—"}</span>
              </div>
              <div className="sm-customer-info__item">
                <strong>{t.phone || "Phone"}:</strong>{" "}
                <span>{initialData?.phone_number || "—"}</span>
              </div>
              <div className="sm-customer-info__item">
                <strong>{t.model || "Model"}:</strong>{" "}
                <span>{initialData?.model_name || "—"}</span>
              </div>
              <div className="sm-customer-info__item">
                <strong>{t.chassis || "Chassis"}:</strong>{" "}
                <span>{initialData?.chassis_no || "—"}</span>
              </div>
              <div className="sm-customer-info__item">
                <strong>{t.scheduledService || "Schedule Service"}:</strong>{" "}
                <span>{initialData?.service_name || "—"}</span>
              </div>
              <div className="sm-customer-info__item">
                <strong>{t.lastServiceHours || "Last Service Hours"}:</strong>{" "}
                <span>{initialData?.last_hours || "—"}</span>
              </div>
            </div>
          </aside>

          {/* --- Content Area --- */}
          <main className="sm-content-area">
            <button
              type="button"
              className="sm-modal-close-button"
              onClick={onHide}
              aria-label="Close"
            >
              <FaTimes />
            </button>
            {selectedSection && ( // Ensure selectedSection is loaded
              <form onSubmit={handleSubmit} className="sm-form">
                <div className="sm-form-card">
                  <h3 className="sm-form-card__title">
                    {selectedSection.title}
                  </h3>
                  <div className="sm-form-card__body">
                    <p className="sm-form-card__description">
                      {t.pleaseFillDetails || "Please fill in the necessary details for"}{" "}
                      <strong>{selectedSection.title}</strong>.
                    </p>

                    {selectedSection.fields.map((field) => (
                      <div key={field.id} className="sm-form-group">
                        <label htmlFor={field.id} className="sm-form-label">
                          {field.label}{" "}
                          {field.required && (
                            <span className="sm-form-label__required-asterisk">
                              *
                            </span>
                          )}
                        </label>
                        {field.type === "select" ? (
                          <select
                            id={field.id}
                            name={field.id}
                            value={formData[field.id] || ""}
                            onChange={handleChange}
                            className="sm-form-control" // Uses the same styling
                            required={field.required}
                          >
                            {field.options.map((option) => (
                              <option
                                key={option.value}
                                value={option.value}
                                disabled={option.value === "" && field.required}
                              >
                                {option.label}
                              </option>
                            ))}
                          </select>
                        ) : field.type === "textarea" ? (
                          <textarea
                            id={field.id}
                            name={field.id}
                            value={formData[field.id] || ""}
                            onChange={handleChange}
                            className="sm-form-control"
                            rows="4"
                            placeholder={
                              t.enterPlaceholder
                                ? t.enterPlaceholder.replace("{field}", field.label.toLowerCase())
                                : `Enter ${field.label.toLowerCase()}...`
                            }
                            required={field.required}
                          />
                        ) : (
                          <input
                            type={field.type}
                            id={field.id}
                            name={field.id}
                            value={formData[field.id] || ""}
                            onChange={handleChange}
                            className="sm-form-control"
                            required={field.required}
                            placeholder={
                              t.enterPlaceholder
                                ? t.enterPlaceholder.replace("{field}", field.label.toLowerCase())
                                : `Enter ${field.label.toLowerCase()}...`
                            }
                          />
                        )}
                      </div>
                    ))}
                  </div>

                  <div className="sm-form-card__footer">
                    <button
                      type="button"
                      className="sm-btn sm-btn--cancel"
                      onClick={onHide}
                    >
                      <FaTimes className="sm-btn__icon--left" /> {t.cancel || "Cancel"}
                    </button>
                    <button type="submit" className="sm-btn sm-btn--save">
                      <FaSave className="sm-btn__icon--left" /> {t.saveData || "Save Data"}
                    </button>
                  </div>
                </div>
              </form>
            )}
          </main>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default ServiceModal;
